// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import BloqueFicha, { TemaFicha } from "../components/funcionales/BloqueFicha";
import BloqueMultas, { money } from "../components/funcionales/BloqueMultas";
import BloqueRadicacion from "../components/funcionales/BloqueRadicacion";
import Patente from "../components/funcionales/Patente";
import ChoferesDelTractor from "../components/funcionales/ChoferesDelTractor";
import ConsultarCENT, {
  useConsultasCENT,
} from "../components/funcionales/ConsultarCENT";
import Icono from "../components/funcionales/IconoFicha";
import LineaVencimientos from "../components/funcionales/LineaVencimientos";
import FormGestor from "../components/formularios/FormGestor";
import { estadoVencimiento, polizaDeUnidad } from "../services/seguros";
import Ubicacion from "../components/funcionales/Ubicacion";
import RepuestosDeUnidad from "../components/taller/RepuestosDeUnidad";
import { repuestosDeUnidad } from "../shared/repuestosColocados";
import VeredictoRTO, { fechaCorta } from "../components/funcionales/VeredictoRTO";
import {
  adblueDe,
  historialDe,
  repuestosDe,
  scaniaDe,
  tallerDe,
  ultimoCruceDe,
  viajesDeUnidad,
} from "../services/ficha";
import {
  consultarYRegistrar,
  marcarMultaPagada,
  marcarTodasPagadas,
  multasDeUnidad,
} from "../services/multas";
import {
  consultarYRegistrarRadicacion,
  radicacionDe,
} from "../services/radicacion";
import {
  borrarRTODeclarada,
  declararRTO,
  estadoRTO,
  proximoTurnoRTO,
  ubicacionDe,
} from "../services/porteria";
import { ultimaConsultaCENT } from "../services/rtoCent";
import {
  TITULO_SCANIA,
  adblue,
  cruce,
  historial,
  instante,
  scania,
  taller,
  viajesUnidad,
} from "../shared/ficha";
import {
  claseDeEmpresa,
  etiquetaCorta as etiquetaEmpresa,
  etiquetaConCuit as empresaConCuit,
  nombreDe as nombreEmpresa,
} from "../shared/empresa";
import { SIN_DATO, estadoLona, etiquetaLona } from "../shared/lona";
import { lineaSeguro, puedeVerSeguro } from "../shared/seguroFicha";
import {
  PERMISO_CONSULTAR_CENT,
  limpiar as limpiarPatente,
  sePuedeConsultar,
} from "../shared/rtoCent";
import { ubicacion } from "../shared/ubicacion";
import {
  COLECCION,
  esTipo,
  identidad,
  numero,
  patente,
  ruta as rutaDeUnidad,
  titulo,
} from "../shared/unidad";
import { vecinos } from "../shared/vecinos";
import { useRealtime } from "../services/store";

// ----------------------------------------------------------------------- estilos
import "./css/fichas-pantalla.css";

/* La ficha de una unidad: todo lo que se sabe del 774, en un lugar.
 *
 * ── QUÉ RESUELVE ─────────────────────────────────────────────────────────
 *
 * Para contestar «¿qué pasa con el 774?» había que abrir cinco pantallas: el
 * RTO, el RTO de acoplados, la ticketera, el mapa y el padrón. Y los datos ya
 * estaban todos adentro del hub — la última ubicación con su fuente, el turno
 * de renovación, los tickets abiertos, el último cruce de portería, si usa
 * urea. **Lo que faltaba no era el dato: era la puerta.**
 *
 * ── NO CARGA NADA, Y ES A PROPÓSITO ──────────────────────────────────────
 *
 * Es de lectura. Cada cosa se sigue cargando donde se cargaba: el turno en la
 * agenda, el ticket en la ticketera, el padrón con el lápiz. Dos lugares para
 * cargar lo mismo obligan a adivinar en cuál, y el día que se acierte el que
 * no es, ese trabajo no aparece en ningún informe — es la lección de los dos
 * talleres y la de la agenda de turnos, y no hay motivo para volver a
 * aprenderla acá.
 *
 * ── UN BLOQUE NO PUEDE LLEVARSE PUESTOS A LOS OTROS ──────────────────────
 *
 * Cada uno pide lo suyo, en su propio `try`, y se dibuja apenas llega. Con una
 * sola consulta, un `taller_tickets` lento dejaría la ficha entera en blanco
 * incluido el RTO, que suele ser a lo que se entró. Es la misma regla que ya
 * está escrita en el RTO de tractores.
 *
 * Y `cargando`, `listo`, `vacío` y `no se pudo` son cuatro estados distintos.
 * «No hay tickets» y «no pude consultar la ticketera» mandan a hacer cosas
 * opuestas: el primero es un camión sin trabajo pendiente y el segundo es un
 * problema del sistema. Dibujados igual, alguien sale a buscar el que no es.
 *
 * ── LO VACÍO NO APARECE (Igna, 02/10: lo mismo para los vehículos) ───────
 *
 * Después de la ficha de la persona: *«si están en "0" o vacías, que no
 * aparezcan»*, y lo más urgente arriba. Por eso los cuatro estados siguen
 * siendo cuatro, pero el vacío ahora se calla en vez de decirse: el bloque
 * no se dibuja, y el tema que se queda sin ninguno tampoco. Lo que no se
 * calla es «no se pudo consultar» — esconderlo sería volver a dibujarlo
 * igual que un «no hay». Y lo que avisa sube a un renglón arriba de todo.
 *
 * ── LA FICHA NO LLEVA PERMISO; CADA BLOQUE SÍ ────────────────────────────
 *
 * Mismo criterio que la home: pedirle un permiso a la ficha dejaría afuera a
 * alguien que sí puede ver una de las cinco cosas. Cada bloque cuelga del
 * permiso de SU pantalla y el que no lo tiene ni siquiera pide el dato.
 *
 * La cabeza —interno, patente, marca— no lleva permiso porque es exactamente
 * lo que el mapa en vivo ya muestra sin pedir ninguno: no deja cargar, ni
 * corregir, ni exportar.
 */

/* ── El bloque, que es la pieza que se repite cinco veces ─────────────────
 *
 * `activo` es el permiso ya resuelto. Apagado no pide nada: no es la pared
 * —eso lo decide Supabase— pero no hay ninguna razón para traer por la red
 * algo que no se va a dibujar.
 */
function useBloque(cargar, activo) {
  const [estado, setEstado] = useState(activo ? "cargando" : "apagado");
  const [dato, setDato] = useState(null);
  const [vuelta, setVuelta] = useState(0);

  useEffect(() => {
    if (!activo) {
      setEstado("apagado");
      return undefined;
    }
    let vivo = true;
    setEstado("cargando");
    /* El `try` envuelve a la LLAMADA y no sólo a la promesa: un `.catch()` no
     * atrapa lo que revienta antes de devolverla. Lo encontró un test en el
     * RTO y vale igual acá. */
    try {
      Promise.resolve(cargar())
        .then((r) => {
          if (!vivo) return;
          setDato(r);
          setEstado("listo");
        })
        .catch(() => vivo && setEstado("error"));
    } catch {
      setEstado("error");
    }
    return () => {
      vivo = false;
    };
  }, [cargar, activo, vuelta]);

  /* Volver a pedir ESTE bloque, sin recargar la ficha entera.
   *
   * Lo usa el RTO después de que alguien escribe la fecha del título: sin
   * esto la pantalla seguiría diciendo «No tiene RTO registrada» arriba de una
   * fecha que se acaba de guardar, y el que la cargó la cargaría de nuevo. Un
   * `window.location.reload()` volvería a pedir los seis bloques para
   * refrescar uno. */
  const recargar = useCallback(() => setVuelta((v) => v + 1), []);

  /* Cambiar el dato del bloque SIN volver a pedirlo. Lo usa el «Consultar
   * ahora» de la RTO: lo que vuelve del CENT ya es la fila de `rto_estado()`,
   * y pedirla otra vez dejaría el bloque en «cargando» medio segundo justo
   * cuando se está esperando leer la respuesta. */
  const cambiar = useCallback((fn) => setDato((d) => fn(d)), []);

  return { estado, dato, recargar, cambiar };
}

/* ¿Las multas no tienen nada que mostrar? Sin actas, y con la última
 * consulta sin problemas. Un «el portal no respondió» NO es un vacío: es no
 * saber, y un bloque que se esconde ahí dibujaría la falla igual que un «no
 * hay» — lo que `BloqueFicha` vino a evitar. */
const sinMultas = (dato) =>
  !(dato?.actas || []).length &&
  !Object.values(dato?.consulta?.estados || {}).some((e) => e && e !== "ok") &&
  !(dato?.consulta?.avisos || []).some(Boolean);

const Unidad = () => {
  const { tipo, interno } = useParams();
  const navegar = useNavigate();
  const { permisos = null } = useOutletContext() || {};

  /* ── ACÁ SE EDITA, Y ES LO QUE UNIFICA LAS DOS PANTALLAS ─────────────────
   *
   * Lo dijo Igna mirando el padrón: *«ese popup no debería existir, nunca. Si
   * apretás en el número de interno te debería llevar a la vista de tarjetas.
   * Todo debería estar en uno solo y dar permisos de escritura/lectura»*.
   *
   * Hasta acá eran dos pantallas para la misma unidad: el panel lateral del
   * padrón —que era el ÚNICO que escribía— y esta ficha, que lo mostraba todo
   * y no dejaba tocar nada. Ahora es una: se entra por el interno y se corrige
   * sin cambiar de lugar.
   *
   * Arranca apagado y no se recuerda entre unidades: a esta pantalla se entra
   * a MIRAR —«¿qué pasa con el 774?»— y una ficha que abre en modo escritura
   * es una corrección de más esperando a pasar. Es el argumento del lápiz del
   * padrón, y acá vale igual. */
  const [editando, setEditando] = useState(false);

  /* Mientras los permisos no llegaron se muestra todo, igual que el rail:
   * parpadear con media ficha durante medio segundo se lee como que faltan
   * datos, no como que faltan permisos. */
  const puede = useCallback(
    (p) => permisos === null || permisos.includes(p),
    [permisos],
  );

  const n = numero(interno);
  const id = identidad(interno);
  const valido = esTipo(tipo) && !!n;

  /* El padrón sale del store de realtime, que el armazón ya tiene cargado
   * para todas las pantallas: pedirlo de nuevo serían 655 filas más en cada
   * ficha que se abre. */
  const padron = useRealtime(valido ? COLECCION[tipo] : "tractores");
  const fila = useMemo(
    /* Por la identidad y no por el número: el playo `143-P` (AF901BT) y el
     * furgón `143` (TRN518) tienen el mismo número, y con `numero()` la ficha
     * del playo mostraba la primera de las dos (Aldo, 29/10, ticket 559). */
    () =>
      valido
        ? (padron || []).find((f) => identidad(f.interno) === id)
        : null,
    [padron, id, valido],
  );

  const dominio = fila?.dominio || null;

  /* La unidad de al lado, del MISMO padrón: 430 internos existen en las dos
   * tablas, así que desde el furgón 774 la siguiente es el furgón 775 y no el
   * tractor. Sale del store que el armazón ya tiene cargado — no cuesta una
   * consulta. */
  const { anterior, siguiente } = useMemo(
    () => (valido ? vecinos(padron, n) : { anterior: null, siguiente: null }),
    [padron, n, valido],
  );

  /* VOLVER ES IR ATRÁS DE VERDAD, no «ir al padrón».
   *
   * A esta ficha se llega desde cinco lugares —el buscador, el RTO, los dos
   * padrones y la agenda— y mandar siempre al padrón haría que cuatro de las
   * cinco veces «volver» te deje en una pantalla en la que no estabas. Con el
   * historial se vuelve a donde estabas.
   *
   * Y si NO hay historial —la ficha abierta desde un link de WhatsApp, que es
   * como la abre medio mundo— retroceder saldría del hub. Ahí sí cae al
   * padrón que corresponde, que es la pantalla de la que esta ficha es el
   * detalle. */
  const volver = useCallback(() => {
    if (window.history.length > 1) navegar(-1);
    else navegar(`/padron/${COLECCION[tipo] || "tractores"}`);
  }, [navegar, tipo]);

  /* Las funciones se memorizan para que el efecto del bloque no vuelva a
   * correr en cada render: sin esto, cada tecla del resto del hub relanzaría
   * las cinco consultas. */
  /* La última consulta al CENT va con el RTO y no en un bloque aparte: es la
   * hora del dato que se está leyendo. `ultimaConsultaCENT` no lanza —si no
   * la puede leer devuelve `null` y la ficha no dice la hora—, así que no
   * puede tirar abajo el veredicto. */
  const traerRTO = useCallback(
    () =>
      Promise.all([
        estadoRTO(dominio),
        proximoTurnoRTO(dominio),
        ultimaConsultaCENT(dominio),
      ]).then(([e, t, c]) => ({ estado: e, turno: t, consulta: c || null })),
    [dominio],
  );
  const traerUbic = useCallback(() => ubicacionDe(tipo, n), [tipo, n]);
  const traerTaller = useCallback(() => tallerDe(tipo, n), [tipo, n]);
  const traerHistorial = useCallback(() => historialDe(tipo, n), [tipo, n]);
  const traerRepuestos = useCallback(() => repuestosDe(tipo, n), [tipo, n]);
  const traerCruce = useCallback(() => ultimoCruceDe(tipo, n), [tipo, n]);
  const traerAdblue = useCallback(() => adblueDe(n), [n]);
  const traerMultas = useCallback(() => multasDeUnidad(dominio), [dominio]);
  const traerRadicacion = useCallback(() => radicacionDe(dominio), [dominio]);
  const traerSeguro = useCallback(() => polizaDeUnidad(dominio), [dominio]);
  const traerViajes = useCallback(() => viajesDeUnidad(tipo, n), [tipo, n]);
  /* El vale de urea —el único odómetro que hoy anota una persona— se pide
   * sólo con el permiso que pide su RLS: sin él la base devuelve cero filas,
   * y eso se dibujaría como «nadie anotó nada». */
  const conVale = puede("ver.combustible") || puede("ver.urea");
  const traerScania = useCallback(
    () => scaniaDe(n, { conVale }),
    [n, conVale],
  );

  /* El RTO necesita la patente, así que se pide sólo cuando el padrón ya
   * contestó. Sin dominio hay dos casos distintos —la unidad no está en el
   * padrón, o está y no tiene chapa cargada (son 64 tractores)— y los dos se
   * dicen abajo, sin salir a consultar algo que no se puede consultar. */
  const bRTO = useBloque(
    traerRTO,
    valido && puede("ver.documentos") && !!dominio,
  );
  /* ── CONSULTAR EL CENT AHORA ──────────────────────────────────────────
   *
   * Aldo, 09/10: «recién hicimos la VTV del furgón 448 y no aparece
   * habilitada en FIBOT». La ficha mostraba lo consultado a las 04:35, antes
   * de la revisión, y no había forma de pedir otra cosa. Igna: lo que se puede
   * resolver en el momento, se resuelve en el momento (ticket #371).
   *
   * Lo que vuelve se pega en el bloque: el veredicto, la línea de
   * vencimientos y el aviso de arriba leen de ahí, así que se mueven los tres
   * juntos. La hora de la consulta se actualiza sólo si el CENT contestó: en
   * un 502 lo último que se sabe sigue siendo lo de antes.
   *
   * SE PEGA SÓLO SI SIGUE SIENDO LA MISMA UNIDAD. La ficha no se desmonta al
   * ir a la anterior o la siguiente —es la misma ruta con otro número—, así
   * que si el CENT contesta por el 448 cuando ya se está mirando el 449, eso
   * es del 448: pegado acá, el 449 diría «Puede salir» con el vencimiento de
   * otro camión. La patente se mira en el momento en que vuelve (por eso la
   * referencia y no el valor del render en que se apretó). */
  const { cambiar: cambiarRTO } = bRTO;
  const dominioActual = useRef(dominio);
  dominioActual.current = dominio;
  const pegarRTO = useCallback(
    (dom, estado, r) =>
      limpiarPatente(dom) === limpiarPatente(dominioActual.current) &&
      cambiarRTO((d) =>
        d
          ? {
              ...d,
              estado,
              consulta:
                r?.ok && r.consultadoEn
                  ? { consultado: r.consultadoEn, error: null }
                  : d.consulta,
            }
          : d,
      ),
    [cambiarRTO],
  );
  const cent = useConsultasCENT(pegarRTO);

  const bUbic = useBloque(traerUbic, valido);
  const bTaller = useBloque(traerTaller, valido && puede("ver.ticketera"));
  /* EL HISTORIAL CUELGA DEL MISMO PERMISO QUE LA TICKETERA, y no de uno nuevo.
   * Es la misma información —qué se le hizo a este camión— vista de otra
   * manera: un permiso propio sería una llave que no abre nada distinto, que
   * es justo lo que se limpió de la pantalla de permisos. */
  const bHist = useBloque(traerHistorial, valido && puede("ver.ticketera"));
  /* Los repuestos cuelgan del mismo permiso que «Qué le hicieron»: son la
     otra mitad de la misma pregunta. */
  const bRep = useBloque(traerRepuestos, valido && puede("ver.ticketera"));
  const bCruce = useBloque(traerCruce, valido && puede("ver.porteria"));
  /* Sólo tractores: un furgón no tiene motor, así que no hay urea que
   * consumir. El bloque no se dibuja en su ficha — vacío se leería como que a
   * ese acoplado le falta un dato. */
  const bAdblue = useBloque(
    traerAdblue,
    valido && tipo === "tractor" && puede("ver.combustible"),
  );
  const bMultas = useBloque(
    traerMultas,
    valido && puede("ver.documentos") && !!dominio,
  );
  const bRadicacion = useBloque(
    traerRadicacion,
    valido && puede("ver.documentos") && !!dominio,
  );
  /* El seguro cuelga del permiso de las PÓLIZAS (`ver.polizas`, o Satelital
   * por default: la regla de la sección Seguros), no del de documentación.
   * Es el que pide la RLS de `polizas_seguro`: sin él la base devuelve cero
   * filas, y eso se dibujaría como «No hay póliza cargada» de una unidad que
   * la tiene. Va igual en la línea de tiempo, junto al RTO. */
  const bSeguro = useBloque(
    traerSeguro,
    valido && puedeVerSeguro(permisos) && !!dominio,
  );
  /* Los últimos viajes salen de `movimientos` (sector Tráfico), así que cuelgan
   * de `ver.trafico`: quien no ve movimientos no los pide. */
  const bViajes = useBloque(traerViajes, valido && puede("ver.trafico"));
  /* Según Scania: sólo tractores (un furgón no tiene Communicator) y con
   * `ver.flota`, el permiso del padrón — es el chasis, el odómetro y el
   * service del camión, lo mismo que se mira ahí. */
  const bScania = useBloque(
    traerScania,
    valido && tipo === "tractor" && puede("ver.flota"),
  );

  if (!valido) {
    return (
      <div className="panel-aviso">
        <h3>Esa unidad no existe</h3>
        <p>
          La dirección tiene que ser de un tractor o de un furgón, con su número
          de interno.
        </p>
      </div>
    );
  }

  const chapa = patente(dominio);
  const t = bTaller.estado === "listo" ? taller(bTaller.dato) : null;
  const c = bCruce.estado === "listo" ? cruce(bCruce.dato, tipo) : null;
  const urea = bAdblue.estado === "listo" ? adblue(bAdblue.dato) : null;
  const hist = bHist.estado === "listo" ? historial(bHist.dato) : [];
  const reps = bRep.estado === "listo" ? repuestosDeUnidad(bRep.dato) : [];
  const viajes = bViajes.estado === "listo" ? viajesUnidad(bViajes.dato) : [];
  const sc = bScania.estado === "listo" ? scania(bScania.dato, fila) : null;

  /* Los vencimientos para la línea de tiempo. El RTO va siempre (toda unidad lo
   * tiene, y "sin dato" es en sí un aviso); el seguro sólo si hay póliza, para
   * no ensuciar la línea con un "sin dato" de algo que capaz ni corresponde.
   * Senasa/tacógrafo/matafuego todavía no son un dato del sistema. */
  const vencItems = [];
  if (bRTO.estado === "listo") {
    vencItems.push({
      clave: "rto",
      label: "RTO",
      dias: bRTO.dato?.estado?.dias ?? null,
    });
  }
  /* La póliza (`{ poliza_nro, vence }`) si se leyó y hay; `null` si se leyó y
   * no hay; `undefined` si no se leyó —error, permiso, sin patente—. */
  const poliza = bSeguro.estado === "listo" ? bSeguro.dato : undefined;
  if (poliza?.vence) {
    vencItems.push({
      clave: "seguro",
      label: "Seguro",
      dias: estadoVencimiento(poliza.vence).dias,
    });
  }

  /* ── Lo que avisa, arriba de todo (Igna, 02/10: lo mismo que la persona) ──
   *
   * Con los bloques ordenados por tema, un aviso queda donde le toca —las
   * multas, en Papeles; el taller, abajo de todo— y el que abre la ficha para
   * saber si hay algo que hacer tendría que recorrerla entera. Así que lo que
   * avisa SE NOMBRA ARRIBA, en un renglón, diciendo en qué bloque está; y el
   * bloque además queda pintado en ámbar en su lugar, para encontrarlo.
   *
   * SÓLO LO QUE LA PANTALLA YA TRAE, ninguna consulta nueva, y sólo lo que
   * ya se pintaba de rojo o de ámbar:
   *
   *   · el RTO con el mismo veredicto que dibuja `VeredictoRTO` — vencido,
   *     por vencer (menos de 30 días: se renueva antes de salir) o sin
   *     ninguna revisión en el CENT. «Todavía no se consultó» NO avisa: se
   *     arregla solo con la sincronización de la noche;
   *   · el seguro, con el mismo `estadoVencimiento` de la línea de tiempo;
   *   · la unidad adentro del taller (la pastilla roja del bloque). Los
   *     tickets abiertos con la unidad afuera NO avisan: son el trabajo de
   *     todos los días de la ticketera, y arriba de todo serían ruido;
   *   · multas impagas o con discrepancia, con el total del bloque;
   *   · sin patente: una sola línea en vez de tres bloques diciendo lo mismo;
   *   · dada de baja. Como en la persona, DE BAJA LOS PAPELES NO AVISAN —un
   *     RTO vencido de un camión que ya no anda no es algo que hacer—; lo que
   *     se debe y lo que está en el taller, sí.
   *
   * CADA AVISO PIDE LA LLAVE DE SU BLOQUE, y la pide sola: un bloque apagado
   * por permiso no llega nunca a «listo», así que su aviso no puede salir. */
  const deBaja = fila?.activo === false;
  const alertas = [];
  if (deBaja)
    alertas.push({
      clave: "baja",
      bloque: "Padrón",
      texto: "está dada de baja: sus vencimientos no cuentan en los avisos.",
    });
  if (!deBaja && fila && !dominio && puede("ver.documentos"))
    alertas.push({
      clave: "patente",
      bloque: "Patente",
      texto:
        "sin patente cargada no hay RTO, multas ni radicación que consultar: los tres van por dominio. Se carga en el padrón.",
    });
  const rto = bRTO.estado === "listo" ? bRTO.dato?.estado : null;
  const turnoRTO = bRTO.estado === "listo" ? bRTO.dato?.turno : null;
  const conTurno = turnoRTO
    ? ` Tiene turno el ${fechaCorta(turnoRTO.fecha)}.`
    : " Sin turno sacado.";
  const textoRTO = !rto
    ? null
    : rto.estado === "vencida"
      ? `no puede salir: vencida hace ${Math.abs(rto.dias)} ${Math.abs(rto.dias) === 1 ? "día" : "días"}.${conTurno}`
      : rto.estado === "por_vencer"
        ? `le ${rto.dias === 1 ? "queda 1 día" : `quedan ${rto.dias} días`}: con menos de 30 se renueva antes de salir.${conTurno}`
        : rto.estado === "sin_rto"
          ? "el CENT no tiene ninguna revisión para esta patente."
          : null;
  if (!deBaja && textoRTO)
    alertas.push({ clave: "rto", bloque: "Revisión técnica", texto: textoRTO });
  if (!deBaja && poliza?.vence) {
    const sv = estadoVencimiento(poliza.vence);
    if (sv.estado === "vencida")
      alertas.push({
        clave: "seguro",
        bloque: "Vencimientos",
        texto: `el seguro venció hace ${Math.abs(sv.dias)} ${Math.abs(sv.dias) === 1 ? "día" : "días"}.`,
      });
    else if (sv.estado === "por_vencer")
      alertas.push({
        clave: "seguro",
        bloque: "Vencimientos",
        texto:
          sv.dias === 0
            ? "el seguro vence hoy."
            : `el seguro vence en ${sv.dias} ${sv.dias === 1 ? "día" : "días"}.`,
      });
  }
  if (t?.adentro)
    alertas.push({
      clave: "taller",
      bloque: "Taller",
      texto:
        "está adentro del taller" +
        (t.desde ? ` desde ${instante(t.desde)}` : "") +
        (t.motivo ? ` · ${t.motivo}` : "") +
        ".",
    });
  if (bMultas.estado === "listo" && bMultas.dato) {
    const actas = bMultas.dato.actas || [];
    const impagas = actas.filter((a) => !a.pagada);
    const conDiscrepancia = actas.filter((a) => a.discrepancia).length;
    const partes = [];
    if (impagas.length)
      partes.push(
        `${impagas.length} impaga${impagas.length === 1 ? "" : "s"} por ${money(
          impagas.reduce((s, a) => s + (Number(a.monto) || 0), 0),
        )}`,
      );
    if (conDiscrepancia)
      partes.push(
        `${conDiscrepancia} marcada${conDiscrepancia === 1 ? "" : "s"} pagada${conDiscrepancia === 1 ? "" : "s"} que el organismo sigue listando`,
      );
    if (partes.length)
      alertas.push({
        clave: "multas",
        bloque: "Multas",
        texto: partes.join(" · ") + ".",
      });
  }
  const alertaRTO = alertas.some((a) => a.clave === "rto");
  const alertaTaller = alertas.some((a) => a.clave === "taller");
  const alertaMultas = alertas.some((a) => a.clave === "multas");
  const seguro = lineaSeguro(poliza);

  return (
    <div className="pf">
      {/* ── MOVERSE: volver, y la unidad de al lado ─────────────────────
          Lo pidió Igna, y son dos cosas distintas que por eso van separadas:
          **volver** es salir de acá —«hoy no es limpia la manera de volver»,
          la única salida era el botón del navegador— y **anterior/siguiente**
          es quedarse adentro recorriendo la flota, que hasta ahora obligaba a
          ir al buscador y tipear el número de al lado. */}
      <nav className="pf-mover" aria-label="Moverse entre unidades">
        <button
          type="button"
          className="pf-mover__btn"
          onClick={volver}
          title="Volver a donde estabas"
        >
          ← Volver
        </button>

        <div className="pf-mover__lados">
          {/* EN LAS PUNTAS SE DESHABILITA Y NO DA LA VUELTA. Saltar del último
              al primero se lee como que la pantalla se movió sola; un botón
              apagado dice que ahí se termina la lista. */}
          <button
            type="button"
            className="pf-mover__btn"
            disabled={!anterior}
            onClick={() => anterior && navegar(rutaDeUnidad(tipo, anterior))}
            title={
              anterior
                ? `Ir al ${anterior} · las dadas de baja se saltean`
                : "No hay ninguna anterior"
            }
          >
            ‹ Anterior
          </button>
          <button
            type="button"
            className="pf-mover__btn"
            disabled={!siguiente}
            onClick={() => siguiente && navegar(rutaDeUnidad(tipo, siguiente))}
            title={
              siguiente
                ? `Ir al ${siguiente} · las dadas de baja se saltean`
                : "No hay ninguna siguiente"
            }
          >
            Siguiente ›
          </button>
        </div>
      </nav>

      {/* ── La cabeza ──────────────────────────────────────────────────── */}
      <header className="pf-cabeza">
        <div className="pf-quien">
          <h2>
            {titulo(tipo, id)}
            {/* DE QUIÉN ES, al lado del interno y con su color.
                Lo pidió Igna. Antes el CUIT pelado —`30610890403`— viajaba en
                el subtítulo entre la marca y el año: once dígitos que no
                identifican nada para el que los lee, cuando el hub ya sabía
                traducirlos desde que existe la columna Empresa del RTO.
                `siglaDe` es la misma traducción, así que la ficha y el RTO no
                pueden decir cosas distintas del mismo camión. */}
            {fila?.empresa && etiquetaEmpresa(fila.empresa) !== "—" && (
              <span
                className={claseDeEmpresa(fila.empresa)}
                title={nombreEmpresa(fila.empresa)}
              >
                {/* EL CUIT AL LADO DE LA SIGLA (pedido de Nico, 28/08/2026).
                    Acá entra porque es la ficha de UNA unidad; en las listas
                    sigue yendo la sigla sola — trece caracteres más por fila
                    convertirían la columna Empresa en la más ancha de la
                    tabla para repetir cuatro números. */}
                {empresaConCuit(fila.empresa)}
              </span>
            )}
          </h2>
          <p className="tk-meta">
            {/* EL CHASIS ENTRA ACÁ, y es lo que se pidió: es el número con el
                que se pide un repuesto y con el que se lo busca en el catálogo
                del fabricante, así que tenerlo a un «abrí el padrón» de
                distancia es lo mismo que no tenerlo. Va con su rótulo: un
                número largo suelto entre la marca y el año se lee como
                cualquier otra cosa. */}
            {[
              fila?.marca,
              fila?.modelo,
              fila?.chasis ? `chasis ${fila.chasis}` : null,
            ]
              .filter((x) => x && x !== "0")
              .join(" · ") || "sin datos del padrón"}
            {fila?.activo === false && " · dado de baja"}
          </p>
          {/* LA PÓLIZA EN LA CABEZA (Aldo, Tráfico, 23/10, ticket 496: «que se
              vea ni bien hago la consulta»). Abajo, en Papeles, quedaba debajo
              de todo el tema Hoy y había que bajar a buscarla. Mismas reglas
              que antes (`lineaSeguro`): sin cargar va en gris y nunca «no
              asegurada»; vencida en ámbar; si no se pudo leer, no va. */}
          {puedeVerSeguro(permisos) && seguro && (
            <p
              className={
                "pf-seguro" +
                (seguro.aviso ? " pf-seguro--aviso" : "") +
                (seguro.neutro ? " tenue" : "")
              }
            >
              {seguro.texto}
            </p>
          )}
        </div>
        {/* LA CHAPA Y «SIN CHAPA» NO SE DIBUJAN IGUAL, y no es un detalle: son
            64 tractores en el padrón sin dominio cargado. Un hueco silencioso
            manda a corregir un interno que estaba bien escrito — es la misma
            distinción que hace `patentes.py` entre «no existe» y «existe sin
            patente».

            Y la chapa va DIBUJADA como chapa Mercosur (Igna, 03/10: «quedan re
            facheras»): es el nombre del camión en la calle, y así se lo
            reconoce sin leerlo. «Sin patente» sigue siendo un `plate-chip`
            común: una chapa en blanco diría que la patente es ésa, vacía. */}
        {chapa ? (
          <Patente dominio={chapa} grande />
        ) : (
          <span
            className="plate-chip"
            title="El padrón no tiene el dominio de esta unidad"
          >
            sin patente
          </span>
        )}

        {/* LA LONA, sólo en un furgón: un tractor no tiene caja, y un «Sin
            dato» ahí se leería como un dato que falta cuando la pregunta no
            aplica. Es el mismo criterio por el que el AdBlue no se dibuja en
            la ficha de un furgón.

            Y va en la cabeza, al lado de la chapa, porque es una propiedad de
            la unidad y no algo que cambia: no es un bloque que se consulta,
            es parte de qué ES este furgón. */}
        {/* EDITAR VIVE EN LA CABEZA, que es donde está la identidad de la
            unidad: la patente, la marca, el año y el chasis se leen ahí, así
            que el botón que los corrige tiene que estar al lado y no abajo de
            cuatro bloques de consulta.

            Se dibuja SÓLO con `cargar.flota` —el mismo permiso que gobierna el
            padrón, así que nadie gana ni pierde acceso: el que hoy corrige un
            tractor lo sigue corrigiendo, y desde acá—. Y como todo en el hub,
            esto no es la pared: lo que de verdad restringe es Supabase. */}
        {fila && puede("cargar.flota") && !editando && (
          <button
            type="button"
            className="pf-mover__btn"
            onClick={() => setEditando(true)}
          >
            ✎ Editar
          </button>
        )}

        {tipo === "furgon" && fila && (
          <span
            className={
              "plate-chip" +
              (estadoLona(fila.lona) === SIN_DATO ? " tenue" : "")
            }
            title={
              estadoLona(fila.lona) === SIN_DATO
                ? "Todavía no se cargó si este furgón tiene lona. Se carga en el padrón."
                : "Cargado en el padrón de furgones"
            }
          >
            {etiquetaLona(fila.lona)}
          </span>
        )}
      </header>

      {/* La unidad puede no estar en el padrón y aun así tener cruces y
          tickets: se avisa y NO se corta la ficha. Contestar «no existe»
          escondería lo que sí se sabe. */}
      {!fila && (
        <p className="pf-nopude">
          Esta unidad no está en el padrón de{" "}
          {tipo === "tractor" ? "tractores" : "furgones"}.
        </p>
      )}

      {/* Lo que avisa, nombrando su bloque. Se esconde al editar, como la
          línea de tiempo y los temas: arriba del formulario sería una lista
          de cosas que desde ahí no se arreglan. */}
      {!editando && alertas.length > 0 && (
        <ul className="pf-alertas" aria-label="Para mirar">
          {alertas.map((a) => (
            <li key={a.clave} className="pf-alerta">
              <Icono nombre="alerta" />
              <span>
                <b>{a.bloque}:</b> {a.texto}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* ── LA LÍNEA DE TIEMPO DE VENCIMIENTOS ───────────────────────────
          Arriba de todo, porque es la lectura de un vistazo —¿qué le vence y
          cuándo?— que hoy había que reconstruir mirando el bloque de RTO y
          abriendo Seguros aparte. Se esconde al editar, como los temas. */}
      {!editando && <LineaVencimientos items={vencItems} />}

      {/* ── LA EDICIÓN REEMPLAZA A LOS BLOQUES, no se suma abajo ─────────
          Es la regla que ya tenía la ficha del padrón: los mismos datos dos
          veces —uno en gris y otro en una casilla— obligan a mirar cuál de los
          dos es el que se está por guardar. Y los bloques de abajo son
          CONSULTA —taller, RTO, cruce, ubicación—: no se editan desde acá
          porque no son columnas del padrón, salen de otras tablas.

          EL FORMULARIO ES EL MISMO (`FormGestor`), no una segunda copia. Ahí
          viven el chequeo de patente repetida y el sellado del autor
          (`Submits.js` → `updateRow`); reescribir el guardado acá los perdería
          a los dos, y el día que difieran nadie va a mirar esta pantalla.

          EL INTERNO NO SE PUEDE CAMBIAR, y no es un olvido: es la clave, y
          `eventos`, `taller_tickets`, `movimientos` y `ubicaciones` lo guardan
          como TEXTO. Renombrarlo no renombra nada de eso — deja diez años de
          historial apuntando a un número que ya no existe en el padrón, sin
          fallar en ningún lado. Ya venía bloqueado con `notChange` y se
          respeta. Se carga al dar de alta, que es cuando todavía no hay nada
          colgando. */}
      {editando && fila && (
        <div className="ficha-edicion">
          <p className="ficha-editando">
            Editando el padrón de {tipo === "tractor" ? "tractores" : "furgones"}.
            Nada cambia hasta apretar Guardar.
          </p>
          <FormGestor
            elemento={fila}
            coleccion={COLECCION[tipo]}
            onGuardar={() => setEditando(false)}
            onClose={() => setEditando(false)}
          />
          <div className="ficha-buttons">
            <button
              type="button"
              className="pf-mover__btn"
              onClick={() => setEditando(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* GUARDAR VUELVE A LECTURA Y NO CIERRA LA PANTALLA, al revés que en la
          ficha del padrón — y la diferencia es real, no un cambio de gusto:
          allá el formulario recibía un `elemento` que quedaba viejo apenas se
          guardaba, así que volver a lectura mostraba los valores de antes.
          Acá `fila` sale del store en vivo, así que lo guardado se ve. */}

      {/* ── Tres temas y no una grilla de diez (Igna, 02/10) ────────────────
          «Me gustaría que lo mismo apliquemos para los vehículos»: la misma
          «opción E» que eligió para la ficha de la persona. Los bloques van
          agrupados por la pregunta que contestan, cada tema con su color y su
          ícono, y adentro SÓLO LO QUE TIENE ALGO:

            · HOY      dónde está y quién la maneja — lo que se pregunta por
                       teléfono: la ubicación, el portón, el chofer, lo que
                       dijo Scania a las 08:00 y los viajes.
            · PAPELES  lo legal — el RTO, que decide si sale, las multas y la
                       radicación. Es lo que más avisa, y sus avisos suben.
            · TALLER   lo mecánico — si está adentro, qué le hicieron, y el
                       AdBlue, que es una pregunta del sistema SCR del motor y
                       no de dónde anda el camión.

          Antes eran diez recuadros iguales en el orden en que se fueron
          agregando —con dos «pares» apilados para que no quedaran flacos— y en
          un furgón sin cruces la mitad decía «no hay».

          Cada bloque lleva `ocultarSiVacio`: el vacío no se dibuja, y un tema
          sin ningún bloque a la vista tampoco (lo resuelve `TemaFicha`).
          «Consultando…» y «no se pudo consultar» SÍ se ven: no son un vacío.
          Lo que es de un solo tipo —el chofer y el AdBlue, sólo tractores— ya
          ni se monta en el otro, y eso no cambió. */}
      <div className="pf-temas" hidden={editando}>
        {/* ── Hoy: dónde está y quién la maneja ─────────────────────────── */}
        <TemaFicha id="hoy" titulo="Hoy" icono={<Icono nombre="ojo" />}>
          {/* ── Dónde estuvo ─────────────────────────────────────────────
              Primero, porque es LA pregunta de este tema. Sin ningún registro
              —ni portería ni un tramo cargado— no se dibuja: un «—» con su
              explicación ocupaba lo mismo que un lugar. */}
          <BloqueFicha
            titulo="Última ubicación registrada"
            estado={bUbic.estado}
            ocultarSiVacio
            vacio={
              bUbic.estado === "listo" && ubicacion(bUbic.dato || {}).vacio
                ? "No hay ningún registro de esta unidad."
                : null
            }
          >
            {/* NUNCA el lugar solo. «Río Grande» sin fecha se lee como «está ahí
              ahora» y puede ser de hace tres semanas — y de dónde salió
              importa igual: portería es de hoy y el movimiento viene
              atrasado. Las dos cosas las pone `Ubicacion`. */}
            <p className="pf-dato">
              <Ubicacion fila={bUbic.dato || {}} />
            </p>
          </BloqueFicha>

          {/* ── Último cruce ─────────────────────────────────────────────
              «Nunca cruzó el portón» ya no se dibuja (Igna, 02/10). Lo que se
              explicaba —que no es un error, que hay unidades sin un solo cruce
              anotado— sigue escrito en `cruce()` para cuando haga falta. */}
          {puede("ver.porteria") && (
            <BloqueFicha
              titulo="Último cruce del portón"
              estado={bCruce.estado}
              ocultarSiVacio
              vacio={c?.vacio ? c.detalle : null}
              comoLlegar={
                <Link className="rto-link" to="/porteria">
                  Ir a portería
                </Link>
              }
            >
              {c && !c.vacio && (
                <>
                  <p className="pf-dato">
                    <span className={"pill " + c.tono}>{c.titulo}</span>{" "}
                    {c.cuando}
                    <em className="tk-item-meta"> · {c.hace}</em>
                  </p>
                  <p className="pf-dato">
                    {c.sinChofer ? (
                      <span className="tenue">
                        Sin chofer anotado en el evento
                      </span>
                    ) : (
                      <>
                        {/* El DNI se muestra aunque el padrón no tenga el
                          nombre: es más de lo que había, y con el número se
                          busca a la persona. */}
                        {c.chofer || `DNI ${c.dni}`}
                        {c.legajo && (
                          <em className="tk-item-meta"> · legajo {c.legajo}</em>
                        )}
                      </>
                    )}
                  </p>
                  {c.con.length > 0 && (
                    <p className="pf-dato">
                      <span className="et">Con el {c.queEsCon}</span>{" "}
                      {c.con.join(", ")}
                    </p>
                  )}
                </>
              )}
            </BloqueFicha>
          )}

          {/* ── Quién lo tiene asignado (pedido de Nico) ──────────────────
              «Cuando se pregunta por YYY te diga que chofer XXX lo tiene
              asignado». Sólo tractores: un furgón no lleva chofer.

              ACÁ NO SE EDITA. La fila es de la persona —su clave es el DNI— y
              se carga en su ficha. Repetir el formulario acá serían dos
              lugares para cargar lo mismo, que es lo que el encabezado de esta
              pantalla dice que no se hace. Lo que sí hay es el enlace a cada
              chofer.

              SIN NADIE ASIGNADO, SIN PROPUESTA DE PORTERÍA Y SIN TARJETA
              AJENA, NO SE DIBUJA (Igna, 02/10). Lo dice `ChoferesDelTractor`
              con `useAvisarVacio`, porque es el único que sabe cuándo llegaron
              sus tres consultas. A diferencia del tractor asignado en la
              ficha de la persona, acá no hay botón que conservar: se asigna
              desde la ficha del chofer.

              EL PERMISO ES EL MISMO QUE LA RLS DE LA TABLA: `ver.flota` **o**
              `ver.gente`. La cabeza de esta ficha no pide permiso a propósito
              —muestra lo que el mapa en vivo ya muestra—, así que sin este
              filtro el que no tiene ninguno de los dos recibiría una lista
              vacía de la base y leería «Nadie lo tiene asignado», que es
              **falso**. Un permiso que falta no se dibuja como un dato que no
              existe. */}
          {tipo === "tractor" && (puede("ver.flota") || puede("ver.gente")) && (
            <BloqueFicha titulo="Chofer asignado" estado="listo" ocultarSiVacio>
              <ChoferesDelTractor
                interno={n}
                puedeVerGente={puede("ver.gente")}
              />
            </BloqueFicha>
          )}

          {/* ── Según Scania (Igna, 09/10; Nico, tickets 354 y 365) ────────────
              Lo que FlotaBot le leyó a My Scania a las 08:00, con LA FECHA EN
              EL TÍTULO: «512.340 km» sin fecha se lee como el tablero de ahora.

              AL LADO DE LO MANUAL, NUNCA EN SU LUGAR. «Los km se guardan los
              dos, no pises lo manual»: el odómetro de Scania va junto al
              anotado en el vale de urea, y el chasis de Scania junto al del
              padrón cuando no coinciden o el padrón no lo tiene (el 1036 y el
              1032 de los tickets de Nico). Cuál vale lo decide una persona en
              el padrón; esta tarjeta no escribe nada.

              Es `pf-bloque` como las demás, así que hereda el vidrio de los
              temas. Sin fila —Scania no ve la unidad— no aparece, igual que
              todo lo vacío de esta ficha (Igna, 02/10). Y mientras la vista
              no esté en la base (la migración se aplica por su lado) tampoco:
              `scaniaDe` lo distingue de «no pude», que sí se ve. */}
          {tipo === "tractor" && puede("ver.flota") && (
            <BloqueFicha
              titulo={sc?.titulo || TITULO_SCANIA}
              estado={bScania.estado}
              ocultarSiVacio
              vacio={sc?.vacio ? sc.detalle : null}
            >
              {sc && !sc.vacio && (
                <>
                  {sc.viejo && (
                    <p className="pf-dato">
                      <span className="pill ojo">{sc.viejo}</span>
                    </p>
                  )}
                  <ul className="pf-papeles">
                    {sc.km && (
                      <li>
                        <span className="et">Odómetro</span>
                        <span className="dato">
                          {sc.km.scania ? (
                            <>
                              {sc.km.scania}{" "}
                              <em className="tk-item-meta">según Scania</em>
                            </>
                          ) : (
                            <span className="tenue">Scania no lo informó</span>
                          )}
                        </span>
                        {sc.km.vale && (
                          <span className="dato">
                            · {sc.km.vale.texto}{" "}
                            <em className="tk-item-meta">
                              anotado en el vale de urea del {sc.km.vale.dia}
                            </em>
                          </span>
                        )}
                        {sc.km.valeFallo && (
                          <em className="tk-item-meta">
                            · el último vale de urea no se pudo consultar
                          </em>
                        )}
                        {sc.km.noCierran && (
                          <span className="pill ojo">⚠ no cierran</span>
                        )}
                      </li>
                    )}
                    {sc.km?.noCierran && (
                      <li className="pf-chico">{sc.km.noCierran}</li>
                    )}
                    {sc.horas && (
                      <li>
                        <span className="et">Horas de motor</span>
                        <span className="dato">{sc.horas}</span>
                      </li>
                    )}
                    {sc.service && (
                      <li>
                        <span className="et">Próximo service</span>
                        {sc.service.pasado ? (
                          <span className="pill mal">{sc.service.texto}</span>
                        ) : (
                          <span className="dato">{sc.service.texto}</span>
                        )}
                        {sc.service.fuente && (
                          <em className="tk-item-meta">
                            según {sc.service.fuente}
                          </em>
                        )}
                      </li>
                    )}
                    {sc.senal && (
                      <li>
                        <span className="et">Última señal</span>
                        <span className="dato">{sc.senal.cuando}</span>
                        {sc.senal.vieja && (
                          <em className="tk-item-meta">
                            · {sc.senal.hace}: los números son de ese momento
                          </em>
                        )}
                      </li>
                    )}
                    {sc.chasis && sc.chasis.estado !== "coincide" && (
                      <li>
                        <span className="et">Chasis</span>
                        <span className="dato">{sc.chasis.scania}</span>
                        <em className="tk-item-meta">según Scania</em>
                        {sc.chasis.estado === "falta" && (
                          <span className="pill gris">
                            el padrón no lo tiene cargado
                          </span>
                        )}
                        {sc.chasis.estado === "difiere" && (
                          <span className="pill ojo">
                            ⚠ el padrón dice «{sc.chasis.padron}»
                          </span>
                        )}
                      </li>
                    )}
                    {sc.vin && (
                      <li>
                        <span className="et">VIN</span>
                        <span className="dato">{sc.vin}</span>
                        {sc.chasis?.estado === "coincide" && (
                          <em className="tk-item-meta">
                            · chasis {sc.chasis.scania}, el mismo del padrón
                          </em>
                        )}
                      </li>
                    )}
                    {sc.equipo && (
                      <li>
                        <span className="et">Equipo</span>
                        <span className="dato">{sc.equipo}</span>
                      </li>
                    )}
                  </ul>
                  {sc.chasis?.estado === "difiere" && (
                    <p className="tenue pf-chico">
                      No se corrige solo: cuál de los dos vale lo decide una
                      persona en el padrón.
                    </p>
                  )}
                  {sc.notas.map((t) => (
                    <p key={t} className="tenue pf-chico">
                      {t}
                    </p>
                  ))}
                </>
              )}
            </BloqueFicha>
          )}

          {/* ── Últimos viajes ───────────────────────────────────────────────
              Salen de `movimientos` por el RPC `viajes_de_unidad`. Es la lectura
              "¿por dónde anduvo este camión?" que antes obligaba a abrir la
              pantalla de Movimientos y filtrar por interno. Al final del tema:
              es lo más largo y lo más viejo de los cuatro. Sin viajes, no
              aparece. */}
          {puede("ver.trafico") && (
            <BloqueFicha
              titulo="Últimos viajes"
              estado={bViajes.estado}
              ocultarSiVacio
              vacio={viajes.length ? null : "Sin viajes registrados."}
            >
              <ul className="pf-viajes">
                {viajes.map((v) => (
                  <li key={v.id} className="pf-viaje">
                    <span className="pf-viaje-fecha">{v.fecha}</span>
                    <span className="pf-viaje-ruta">{v.ruta}</span>
                    {v.chofer && (
                      <span className="pf-viaje-chofer">{v.chofer}</span>
                    )}
                  </li>
                ))}
              </ul>
            </BloqueFicha>
          )}
        </TemaFicha>

        {/* ── Papeles: lo legal ─────────────────────────────────────────── */}
        <TemaFicha id="papeles" titulo="Papeles" icono={<Icono nombre="carpeta" />}>
          {/* ── RTO ──────────────────────────────────────────────────────
              Primero del tema: decide si el camión sale. Con patente SIEMPRE
              dice algo —hasta «todavía no se consultó» es un veredicto—, así
              que nunca se esconde. Sin patente se esconde, y lo dice una sola
              vez el aviso de arriba en vez de tres bloques repitiendo que el
              CENT, DNRPA y los organismos van por dominio. */}
          {puede("ver.documentos") && (
            <BloqueFicha
              titulo="Revisión técnica"
              estado={dominio ? bRTO.estado : "listo"}
              ocultarSiVacio
              alerta={alertaRTO}
              vacio={
                dominio
                  ? null
                  : "Sin patente cargada no hay RTO que consultar: el CENT va por dominio. Se carga en el padrón."
              }
              comoLlegar={
                <Link className="rto-link" to="/rto">
                  Ir al RTO
                </Link>
              }
            >
              {bRTO.dato && (
                <VeredictoRTO
                  estado={bRTO.dato.estado}
                  turno={bRTO.dato.turno}
                  /* Sin historial: acá compite con los otros bloques, y quién
                   renueva sobre la hora es una pregunta de la pantalla del
                   RTO, no de esta. */
                  conHistorial={false}
                  /* SE LLEVA LA UNIDAD PUESTA. Lo pidió Nico: el alta abría en
                   * blanco y había que volver a tipear el interno y la patente
                   * que estaban en pantalla — y tipeados de nuevo es donde se
                   * cuela el dedazo, con el turno quedando a nombre de otro
                   * camión. Sólo queda poner lo que se va a PROGRAMAR: fecha,
                   * hora y planta.
                   *
                   * `dominio` puede no estar —64 tractores sin chapa cargada— y
                   * entonces ese campo va vacío y el otro no: media ayuda es
                   * mejor que ninguna, y nada se inventa. */
                  onAgenda={() =>
                    navegar(
                      "/rto?vista=agenda" +
                        (interno
                          ? `&interno=${encodeURIComponent(interno)}`
                          : "") +
                        (dominio
                          ? `&patente=${encodeURIComponent(dominio)}`
                          : ""),
                    )
                  }
                  /* LA FECHA DEL TÍTULO, para los 0 km que el CENT todavía no
                   * conoce. Pide `cargar.documentos` —el permiso de EDITAR esa
                   * pantalla— y no `ver.documentos`, que es el que abre este
                   * bloque: mirar el vencimiento y escribirlo son dos cosas.
                   *
                   * El botón se esconde sin el permiso Y la base lo verifica
                   * igual: esconderlo es comodidad, la RPC se puede llamar
                   * desde la consola. */
                  dominio={dominio}
                  puedeCargar={puede("cargar.documentos")}
                  onDeclarar={async (dom, vence, motivo) => {
                    await declararRTO(dom, vence, motivo);
                    bRTO.recargar();
                  }}
                  onBorrarDeclarada={async (dom) => {
                    await borrarRTODeclarada(dom);
                    bRTO.recargar();
                  }}
                >
                  {/* LA HORA DEL DATO Y EL BOTÓN, JUNTOS. «Consultado hoy
                      04:35» al lado de una revisión hecha al mediodía dice
                      solo por qué no aparece, y el botón de al lado dice qué
                      hacer. Sin patente no va: el veredicto ya dice que hay
                      que cargarla. */}
                  {puede(PERMISO_CONSULTAR_CENT) &&
                    sePuedeConsultar({
                      dominio,
                      estado: bRTO.dato.estado?.estado,
                    }) && (
                      <div className="msg-datos">
                        <div>
                          <span className="et">CENT</span>
                          <span>
                            {/* Si la última vez no se pudo, se dice así y
                                no «Consultado»: `error` cubre el CENT caído
                                pero también una respuesta que no se pudo
                                leer o guardar, y en ninguno de los tres
                                casos el dato es de esa hora. */}
                            {bRTO.dato.consulta?.consultado && (
                              <span className="tenue">
                                {bRTO.dato.consulta.error
                                  ? `Se intentó consultar ${instante(bRTO.dato.consulta.consultado)} y no se pudo`
                                  : `Consultado ${instante(bRTO.dato.consulta.consultado)}`}
                                {" · "}
                              </span>
                            )}
                            <ConsultarCENT
                              dominio={dominio}
                              consulta={cent.de(dominio)}
                              onConsultar={cent.consultar}
                              boton="boton"
                            />
                          </span>
                        </div>
                      </div>
                    )}
                </VeredictoRTO>
              )}
            </BloqueFicha>
          )}

          {/* ── Multas ───────────────────────────────────────────────────
              PARA QUIEN PUEDE CONSULTAR SE QUEDA SIEMPRE, aunque no haya
              actas: no es un dato guardado que pueda estar vacío sino una
              herramienta — el bloque es el botón de «Consultar ahora».
              Esconderlo «porque no hay multas» sería esconder la única forma
              de averiguar si las hay. Es lo que hace la ficha de la persona
              con las multas por DNI.

              PARA QUIEN SÓLO MIRA, sin actas es un vacío y no se dibuja —
              salvo que la última consulta haya vuelto con problemas («el
              portal no respondió»): eso no es «no hay», es no saber. */}
          {puede("ver.documentos") && (
            <BloqueFicha
              titulo="Multas"
              estado={dominio ? bMultas.estado : "listo"}
              ocultarSiVacio
              alerta={alertaMultas}
              vacio={
                !dominio
                  ? "Sin patente cargada."
                  : !bMultas.dato ||
                      (!puede("cargar.documentos") && sinMultas(bMultas.dato))
                    ? "Sin multas registradas."
                    : null
              }
            >
              {bMultas.dato && (
                <BloqueMultas
                  dominio={dominio}
                  filas={bMultas.dato.actas}
                  consulta={bMultas.dato.consulta}
                  puedeConsultar={puede("cargar.documentos")}
                  onConsultar={async () => {
                    await consultarYRegistrar(dominio);
                    bMultas.recargar();
                  }}
                  onMarcarPagada={async (id, pagada) => {
                    await marcarMultaPagada(id, pagada);
                    bMultas.recargar();
                  }}
                  onMarcarTodasPagadas={async () => {
                    await marcarTodasPagadas(dominio);
                    bMultas.recargar();
                  }}
                />
              )}
            </BloqueFicha>
          )}

          {/* ── Radicación ───────────────────────────────────────────────
              Mismo criterio que las multas: con el botón de consultar se
              queda aunque nunca se haya consultado; sin el botón y sin dato,
              no aparece. Al final del tema porque no vence ni avisa. */}
          {puede("ver.documentos") && (
            <BloqueFicha
              titulo="Radicación"
              estado={dominio ? bRadicacion.estado : "listo"}
              ocultarSiVacio
              vacio={
                !dominio
                  ? "Sin patente cargada no hay radicación que consultar: DNRPA va por dominio."
                  : !bRadicacion.dato && !puede("cargar.documentos")
                    ? "Nunca se consultó la radicación."
                    : null
              }
            >
              <BloqueRadicacion
                dominio={dominio}
                dato={bRadicacion.dato}
                puedeConsultar={puede("cargar.documentos")}
                onConsultar={async () => {
                  await consultarYRegistrarRadicacion(dominio);
                  bRadicacion.recargar();
                }}
              />
            </BloqueFicha>
          )}
        </TemaFicha>

        {/* ── Taller: lo mecánico ───────────────────────────────────────── */}
        <TemaFicha id="taller" titulo="Taller" icono={<Icono nombre="llave" />}>
          {/* ── Ahora: la estadía y los tickets abiertos ─────────────────────
              Primero del tema: si está adentro ahora es lo único de acá que
              cambia lo que se puede hacer hoy con el camión, y por eso además
              sube a los avisos. Afuera y sin tickets, no aparece.

              SE LLAMA «AHORA» Y NO «TALLER»: adentro del tema Taller, un
              bloque «Taller» era el mismo título dos veces, y la pregunta que
              contesta es la de hoy al lado de «Qué le hicieron», que es la de
              siempre. */}
          {puede("ver.ticketera") && (
            <BloqueFicha
              titulo="Ahora"
              estado={bTaller.estado}
              ocultarSiVacio
              alerta={alertaTaller}
              vacio={
                t?.vacio
                  ? "No está en el taller y no tiene tickets abiertos."
                  : null
              }
              comoLlegar={
                <Link className="rto-link" to="/panel/ticketera">
                  Ir a la ticketera
                </Link>
              }
            >
              {t && (
                <>
                  {/* ADENTRO DEL TALLER Y TICKET ABIERTO NO SON LO MISMO. Un
                      camión puede estar adentro sin ticket cargado, y puede
                      tener tickets abiertos estando afuera —salió sin
                      repararse—. Fundidos, el segundo caso figuraría adentro
                      del taller y portería lo dejaría salir creyendo que el
                      sistema se equivocó. */}
                  {t.adentro && (
                    <p className="pf-dato">
                      <span className="pill mal">En el taller</span>{" "}
                      {t.motivo || "sin motivo anotado"}
                      {t.lugar ? ` · ${t.lugar}` : ""}
                      {t.desde && (
                        <em className="tk-item-meta">
                          {" "}
                          · desde {instante(t.desde)}
                        </em>
                      )}
                    </p>
                  )}
                  {t.abiertos.length > 0 && (
                    <ul className="pf-lista">
                      {t.abiertos.map((k) => (
                        <li key={k.id}>
                          <span
                            className={"pill " + (k.empezado ? "ojo" : "gris")}
                          >
                            {k.empezado ? "en curso" : "esperando"}
                          </span>
                          <Link to={`/taller/ticket/${k.id}`}>
                            {k.tarea || "sin tarea anotada"}
                          </Link>
                          {/* El mecánico se pide al arrancar y no al crear, así
                              que un ticket sin nombre es lo normal y no un
                              hueco: decirlo evita que alguien salga a
                              completarlo. */}
                          <em className="tk-item-meta">
                            {k.mecanico || "sin mecánico todavía"}
                          </em>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </BloqueFicha>
          )}

          {/* ── El historial: qué le hicieron, de las dos fuentes ──────────
              Sin ningún trabajo anotado, no aparece (Igna, 02/10). */}
          {puede("ver.ticketera") && (
            <BloqueFicha
              titulo="Qué le hicieron"
              estado={bHist.estado}
              ocultarSiVacio
              vacio={
                bHist.estado === "listo" && hist.length === 0
                  ? "Todavía no hay ningún trabajo anotado en esta unidad."
                  : null
              }
            >
              {hist.length > 0 && (
                <ul className="pf-lista pf-historial">
                  {hist.map((h) => (
                    <li key={h.clave}>
                      {/* DE DÓNDE SALIÓ VA PRIMERO Y SIEMPRE. Un trabajo de
                          puerto es alguien declarando lo que hizo y un ticket es
                          una reparación con seguimiento: sin la marca, el
                          primero se lee como el segundo y alguien esperaría
                          poder verle el estado. */}
                      <span
                        className={
                          "pill " + (h.fuente === "puerto" ? "gris" : "ojo")
                        }
                      >
                        {h.fuenteTexto}
                      </span>
                      {/* Un ticket abre su detalle, con los repuestos que se
                          le colocaron. Los de puerto y los del taller viejo no
                          tienen a dónde ir. */}
                      {h.fuente === "ticketera" ? (
                        <Link to={`/taller/ticket/${h.id}`}>
                          {h.tarea || "sin tarea anotada"}
                        </Link>
                      ) : (
                        <span>{h.tarea || "sin tarea anotada"}</span>
                      )}
                      <em className="tk-item-meta">
                        {instante(h.cuando)}
                        {h.quien ? ` · ${h.quien}` : ""}
                        {/* El estado sale SÓLO donde existe. Dibujar «hecho» en
                            los de puerto para que las dos columnas coincidan
                            sería inventarles una máquina de estados que no
                            tienen. */}
                        {h.tieneEstado ? ` · ${h.estado}` : ""}
                      </em>
                    </li>
                  ))}
                </ul>
              )}
            </BloqueFicha>
          )}

          {/* ── Repuestos colocados: CantApp y la ticketera (Igna, 22/10) ──
              Cada pieza que se le puso, la más nueva arriba. Incluye los
              eventos viejos SIN detalle, que «Qué le hicieron» no muestra: acá
              importa la pieza, no la frase. Sin ninguna, no aparece. */}
          {puede("ver.ticketera") && (
            <BloqueFicha
              titulo="Repuestos colocados"
              estado={bRep.estado}
              ocultarSiVacio
              vacio={
                bRep.estado === "listo" && reps.length === 0
                  ? "No hay repuestos anotados en esta unidad."
                  : null
              }
            >
              {reps.length > 0 && <RepuestosDeUnidad filas={reps} />}
            </BloqueFicha>
          )}

          {/* ── AdBlue ─────────────────────────────────────────────────────
              VA EN TALLER Y NO EN HOY: si el motor tiene sistema SCR es una
              pregunta de mecánica —qué se le carga, qué se le revisa—, no de
              dónde anda el camión. Sólo tractores: un furgón no tiene motor.

              Sin ninguna fila en el padrón de AdBlue no se dibuja (Igna,
              02/10): no hay nada que decir. Lo que NO cambia es que «no
              sabemos» nunca se dice como «no usa» — si la fila está y no se
              pudo confirmar, el bloque sale y lo dice. */}
          {tipo === "tractor" && puede("ver.combustible") && (
            <BloqueFicha
              titulo="AdBlue"
              estado={bAdblue.estado}
              ocultarSiVacio
              vacio={
                bAdblue.estado === "listo" && !bAdblue.dato
                  ? urea?.detalle
                  : null
              }
            >
              {urea && (
                <>
                  <p className="pf-dato">
                    <span className={"pill " + urea.tono}>{urea.texto}</span>
                  </p>
                  {/* «NO SABEMOS» NO SE DICE COMO «NO USA»: son 86 unidades que
                    todavía no reportaron, y darlas por sin sistema manda a
                    revisar un tanque que funciona. */}
                  <p className="tenue pf-chico">{urea.detalle}</p>
                </>
              )}
            </BloqueFicha>
          )}
        </TemaFicha>
      </div>
    </div>
  );
};

export default Unidad;
